import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { Tabs } from '@factorialco/f0-react/dist/experimental'
import { ChartLine, Laptop, MoneyBag, Suitcase } from '@factorialco/f0-react/icons/app'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import { FeatureFlagAvailabilitiesDocument } from 'generated/resources/graphql'
import { compact } from 'lodash'

import Loading from 'modules/core/components/Loading'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import CompetencyTag from 'modules/job_catalog/components/CompetencyTag'
import EquipmentTag from 'modules/job_catalog/components/EquipmentTag'
import { NodeHeader } from 'modules/job_catalog/components/NodeHeader'
import SalariesCard from 'modules/job_catalog/components/TreeNodePage/SalariesCard'
import WorkingConditionsCard from 'modules/job_catalog/components/TreeNodePage/WorkingConditionsCard'
import CompetenciesAttributes from 'modules/job_catalog/components/TreeNodeAttributes/CompetenciesAttributes'
import OverviewAttributes from 'modules/job_catalog/components/TreeNodeAttributes/OverviewAttributes'
import WorkingConditionsAttributes from 'modules/job_catalog/components/TreeNodeAttributes/WorkingConditionsAttributes'
import { useJobCatalogTreeVisibility } from 'modules/job_catalog/hooks/useJobCatalogTreeVisibility'
import { useTreeNode } from 'modules/job_catalog/hooks/useTreeNode'
import {
  CategoryType,
  DEFAULT_CATEGORIES,
  NODE_TYPES,
  getCategoryTranslation,
  normalizeNodeType,
} from 'modules/job_catalog/lib/nodesUtils'
import { getCompetenciesForDisplay } from 'modules/job_catalog/lib/competencyUtils'
import { getSalariesGroupedByWorkplace } from 'modules/job_catalog/lib/salaryUtils'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

const TreeNodePage = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { id } = useParams<{ id: string }>()

  const { canSeeCompetencies, canSeeItManagement } = useJobCatalogTreeVisibility()

  const [activeTab, setActiveTab] = useState<CategoryType | null>(null)
  const {
    nodeName,
    nodeType,
    currentNode,
    currentAttributes,
    competencyAttributes,
    salaries,
    childrenInformation,
    workingConditions,
    equipmentDisplayItems,
    loading,
  } = useTreeNode(id)

  const { company } = useCurrentSession()
  const { data: featureFlagsData } = useQuery(FeatureFlagAvailabilitiesDocument, {
    fetchPolicy: 'cache-first',
    variables: {
      companyId: company.id,
      featureNames: ['dev_job_catalog_tree_sidepanel'],
    },
  })

  const tabs = useMemo(
    () =>
      compact([
        normalizeNodeType(nodeType) !== NODE_TYPES.LEVEL && DEFAULT_CATEGORIES.OVERVIEW,
        canSeeCompetencies && DEFAULT_CATEGORIES.COMPETENCIES,
      ]),
    [nodeType, canSeeCompetencies]
  )

  useEffect(() => {
    const firstTab = tabs[0]
    if (firstTab) setActiveTab(firstTab)
  }, [tabs])

  const workingConditionsTitle = getCategoryTranslation(
    DEFAULT_CATEGORIES.WORKING_CONDITIONS,
    normalizeNodeType(nodeType)
  )

  const salariesTitle = getCategoryTranslation(
    DEFAULT_CATEGORIES.SALARIES,
    normalizeNodeType(nodeType)
  )

  const groupedSalaries = useMemo(() => getSalariesGroupedByWorkplace(salaries), [salaries])

  const competencyDisplayItems = useMemo(
    () => getCompetenciesForDisplay(competencyAttributes, nodeType),
    [competencyAttributes, nodeType]
  )

  if (!id) return null

  const handleTabChange = (tabId: string) => {
    const newTab = tabs.find((tab) => tab === tabId)
    if (newTab) setActiveTab(newTab)
  }

  const useNewJobCatalogVersion =
    featureFlagsData?.features?.featuresConnection?.nodes?.find(
      (feature) => feature.name === 'dev_job_catalog_tree_sidepanel'
    )?.enabled ?? false

  if (useNewJobCatalogVersion) {
    return (
      <Sidepanel
        open
        onClose={() => navigate(navigation.employees.jobCatalog.overview.fullPath())}
        width="wide"
        title={nodeName ?? ''}
        boxPadding={false}
      >
        {loading ? (
          <Loading expand />
        ) : !currentNode ? null : (
          <>
            <NodeHeader
              name={currentNode.name}
              description={currentNode.description || ''}
              type={currentNode.type}
              childrens={currentNode.children}
              salaries={salaries}
              canEditRole={false}
              currentNode={currentNode}
              isMaximized
            />
            <Box gap="s16" height="full-view-height">
              <Box gap="s16">
                <Box
                  flexDirection="row"
                  alignItems="center"
                  gap="s8"
                  paddingTop="s16"
                  paddingRight="s24"
                  paddingLeft="s24"
                >
                  <Suitcase width={20} height={20} />
                  <Text.Normal weight="semibold" color="grey1000" size="300">
                    {workingConditionsTitle}
                  </Text.Normal>
                </Box>
                <Box paddingRight="s24" paddingLeft="s24">
                  <WorkingConditionsCard workingConditions={workingConditions} />
                </Box>
              </Box>
              <Box gap="s16">
                <Box
                  flexDirection="row"
                  alignItems="center"
                  gap="s8"
                  paddingTop="s16"
                  paddingRight="s24"
                  paddingLeft="s24"
                >
                  <MoneyBag width={20} height={20} />
                  <Text.Normal weight="semibold" color="grey1000" size="300">
                    {salariesTitle}
                  </Text.Normal>
                </Box>
                {groupedSalaries.length > 0 ? (
                  <Box paddingRight="s24" paddingLeft="s24">
                    <SalariesCard groupedSalaries={groupedSalaries} />
                  </Box>
                ) : (
                  <Box paddingRight="s24" paddingLeft="s24">
                    <Text.Normal size="200" color="grey800">
                      {i18n.t('job_catalog.nodes.empty_state.salaries.title')}
                    </Text.Normal>
                  </Box>
                )}
              </Box>
              <Box gap="s16">
                <Box
                  flexDirection="row"
                  alignItems="center"
                  gap="s8"
                  paddingTop="s16"
                  paddingRight="s24"
                  paddingLeft="s24"
                >
                  <ChartLine width={20} height={20} />
                  <Text.Normal weight="semibold" color="grey1000" size="300">
                    {getCategoryTranslation(DEFAULT_CATEGORIES.COMPETENCIES)}
                  </Text.Normal>
                </Box>
                {canSeeCompetencies ? (
                  competencyDisplayItems.length > 0 ? (
                    <Box paddingRight="s24" paddingLeft="s24">
                      <Box
                        border={{ all: { width: 's1', style: 'solid', color: 'grey200' } }}
                        borderRadius={{ all: 'abs016' }}
                        paddingTop="s16"
                        paddingRight="s16"
                        paddingBottom="s16"
                        paddingLeft="s16"
                      >
                        <Box flexDirection="row" flexWrap="wrap" gap="s8">
                          {competencyDisplayItems.map((item) => (
                            <CompetencyTag
                              key={item.id}
                              name={item.name}
                              isInherited={item.isInherited}
                              inheritedFromName={item.inheritedFromName}
                            />
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Box paddingRight="s24" paddingLeft="s24">
                      <Text.Normal size="200" color="grey800">
                        {i18n.t('job_catalog.nodes.empty_state.competency.no_assigned')}
                      </Text.Normal>
                    </Box>
                  )
                ) : (
                  <Box
                    background="grey100"
                    borderRadius={{ all: 'abs016' }}
                    paddingTop="s16"
                    paddingRight="s16"
                    paddingBottom="s16"
                    paddingLeft="s16"
                    gap="s12"
                    style={{ width: '90%', alignSelf: 'center' }}
                  >
                    <Text.Normal size="200" color="grey800">
                      {i18n.t('job_catalog.nodes.empty_state.competency.upsell.description')}
                    </Text.Normal>
                    <UpsellingKit.UpsellButton
                      moduleRequested={AvailableModules.competencies}
                      featureRequested={Features.competencies_core}
                      requestReason={ValidReasons.MissingAddon}
                      requestSource="job_catalog_competencies_sidepanel"
                      pageSource="job_catalog_competencies_sidepanel"
                      label={i18n.t('job_catalog.nodes.empty_state.competency.upsell.button_label')}
                      size="sm"
                    />
                  </Box>
                )}
              </Box>
              <Box gap="s16">
                <Box
                  flexDirection="row"
                  alignItems="center"
                  gap="s8"
                  paddingTop="s16"
                  paddingRight="s24"
                  paddingLeft="s24"
                >
                  <Laptop width={20} height={20} />
                  <Text.Normal weight="semibold" color="grey1000" size="300">
                    {i18n.t('job_catalog.nodes.it_management.one')}
                  </Text.Normal>
                </Box>
                {canSeeItManagement ? (
                  equipmentDisplayItems.length > 0 ? (
                    <Box paddingRight="s24" paddingLeft="s24">
                      <Box
                        border={{ all: { width: 's1', style: 'solid', color: 'grey200' } }}
                        borderRadius={{ all: 'abs016' }}
                        paddingTop="s16"
                        paddingRight="s16"
                        paddingBottom="s16"
                        paddingLeft="s16"
                      >
                        <Box flexDirection="row" flexWrap="wrap" gap="s8">
                          {equipmentDisplayItems.map((item) => (
                            <EquipmentTag key={item.id} item={item} />
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Box paddingRight="s24" paddingLeft="s24">
                      <Text.Normal size="200" color="grey800">
                        {i18n.t('job_catalog.nodes.empty_state.it_management.title')}
                      </Text.Normal>
                    </Box>
                  )
                ) : (
                  <Box
                    background="grey100"
                    borderRadius={{ all: 'abs016' }}
                    paddingTop="s16"
                    paddingRight="s16"
                    paddingBottom="s16"
                    paddingLeft="s16"
                    gap="s12"
                    style={{ width: '90%', alignSelf: 'center' }}
                  >
                    <Text.Normal size="200" color="grey800">
                      {i18n.t('job_catalog.nodes.empty_state.it_management.upsell.description')}
                    </Text.Normal>
                    <UpsellingKit.UpsellButton
                      moduleRequested={AvailableModules.it_management}
                      featureRequested={Features.it_management_v2_core}
                      requestReason={ValidReasons.MissingFeature}
                      requestSource="job_catalog_equipment_sidepanel"
                      pageSource="job_catalog_equipment_sidepanel"
                      label={i18n.t(
                        'job_catalog.nodes.empty_state.it_management.upsell.button_label'
                      )}
                      size="sm"
                      showModuleModal
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </>
        )}
      </Sidepanel>
    )
  }

  return (
    <Sidepanel
      onClose={() => navigate(navigation.employees.jobCatalog.overview.fullPath())}
      open
      width="wide"
      title={nodeName ?? ''}
      boxPadding={false}
    >
      {loading ? (
        <Loading expand />
      ) : !currentNode ? null : (
        <>
          <NodeHeader
            name={currentNode.name}
            description={currentNode.description || ''}
            type={currentNode.type}
            childrens={currentNode.children}
            salaries={salaries}
            canEditRole={false}
            currentNode={currentNode}
            isMaximized
          />
          <Box gap="s16" height="full-view-height">
            {tabs.length > 0 && (
              <Tabs
                tabs={tabs.map((value) => ({
                  label: getCategoryTranslation(value, normalizeNodeType(nodeType)) ?? '',
                  id: value,
                }))}
                setActiveTabId={handleTabChange}
                activeTabId={activeTab ?? undefined}
              />
            )}
            <Box height="full">
              {activeTab === DEFAULT_CATEGORIES.COMPETENCIES && (
                <CompetenciesAttributes
                  items={currentAttributes.filter((item) => item.type === activeTab)}
                />
              )}
              {activeTab === DEFAULT_CATEGORIES.WORKING_CONDITIONS && (
                <WorkingConditionsAttributes
                  items={currentAttributes.filter((item) => item.type === activeTab)}
                />
              )}
              {activeTab === DEFAULT_CATEGORIES.OVERVIEW && (
                <OverviewAttributes
                  currentNode={currentNode}
                  childrenInformation={childrenInformation}
                />
              )}
            </Box>
          </Box>
        </>
      )}
    </Sidepanel>
  )
}

export default TreeNodePage
